import { useEffect, useRef, useState } from 'react';
import { useT } from '../i18n';
import { absolute, canonicalPath } from '../seo';

/**
 * SDÍLENÍ PRODUKTU A ČLÁNKU (#31).
 *
 * ŽÁDNÝ CIZÍ SKRIPT. Oficiální tlačítka Facebooku a spol. jsou kus cizího
 * JavaScriptu, který se načte KAŽDÉMU návštěvníkovi a sleduje ho i tehdy,
 * když nikdy neklikne. Obchod o to nestojí. Sdílet se dá obyčejným odkazem
 * na sdílecí adresu sítě — funguje to stejně a do kliknutí neodejde ven
 * ani bajt.
 *
 * SDÍLÍ SE KANONICKÁ ADRESA, ne `window.location.href`. Kdyby se poslala
 * adresa z prohlížeče, letěly by s ní i filtry a řadicí parametry
 * (`?razeni=cena&skladem=1`) — a všechny nasdílené odkazy na jedno zboží by
 * se rozpadly na desítky různých adres. `canonicalPath()` je totéž, co už
 * stránka posílá v `canonical` a `og:url` (`seo.ts`), takže náhled u sdílení
 * odpovídá tomu, na co odkaz míří.
 */

interface ShareButtonsProps {
  /** Co se sdílí — nadpis produktu nebo článku. */
  readonly title: string;
}

export function ShareButtons({ title }: ShareButtonsProps) {
  const { t } = useT();
  const [url, setUrl] = useState('');
  const [copied, setCopied] = useState<'idle' | 'done' | 'manual'>('idle');
  const resetTimer = useRef<number | undefined>(undefined);
  const manualRef = useRef<HTMLInputElement | null>(null);

  /*
   * Adresa se čte až po vykreslení: `canonicalPath()` sahá na `window`
   * a při každé změně stránky (i jen `?kategorie=`) musí být čerstvá.
   */
  useEffect(() => {
    setUrl(absolute(canonicalPath()));
    setCopied('idle');
  }, [title]);

  // Časovač vracející „Zkopírováno" zpět. Ruší se při odmontování, ať
  // nesahá na stav stránky, která už není.
  useEffect(() => () => window.clearTimeout(resetTimer.current), []);

  // Když ani jeden způsob kopírování neprošel, vysype se adresa do políčka
  // a rovnou se označí — zákazník ji zkopíruje sám.
  useEffect(() => {
    if (copied !== 'manual') return;
    const field = manualRef.current;
    if (field === null) return;
    field.focus();
    field.select();
  }, [copied]);

  if (url === '') return null;

  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);

  async function copyLink() {
    const ok = await copyToClipboard(url);
    setCopied(ok ? 'done' : 'manual');
    if (!ok) return;
    window.clearTimeout(resetTimer.current);
    resetTimer.current = window.setTimeout(() => setCopied('idle'), 2000);
  }

  return (
    <div className="share" aria-labelledby="share-heading">
      <span className="muted small" id="share-heading">
        {t('share.heading')}
      </span>

      {/* `chips`/`chip` je hotový řádek pilulek ze stylopisu — stejný, jaký
          nese výběr varianty nad tím. Žádné nové CSS kvůli čtyřem tlačítkům. */}
      <div className="chips share-buttons">
        <ShareLink
          className="chip link share-button"
          href={`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`}
          label={t('share.facebook')}
        />
        <ShareLink
          className="chip link share-button"
          href={`https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`}
          label={t('share.x')}
        />
        {/* `mailto:` otevře poštovního klienta zákazníka. Nic se neodesílá
            z prohlížeče a e-mailová adresa se nikam nezapisuje. */}
        <a
          className="chip link share-button"
          href={`mailto:?subject=${encodedTitle}&body=${encodedUrl}`}
        >
          {t('share.email')}
        </a>
        <button type="button" className="chip share-button" onClick={() => void copyLink()}>
          {copied === 'done' ? t('share.copied') : t('share.copy')}
        </button>
      </div>

      {copied === 'manual' && (
        <label className="field share-manual">
          <span>{t('share.copyManual')}</span>
          <input
            ref={manualRef}
            type="text"
            readOnly
            value={url}
            onFocus={(event) => event.target.select()}
          />
        </label>
      )}
    </div>
  );
}

function ShareLink({
  className,
  href,
  label,
}: {
  readonly className: string;
  readonly href: string;
  readonly label: string;
}) {
  return (
    <a className={className} href={href} target="_blank" rel="noopener noreferrer">
      {label}
    </a>
  );
}

/**
 * ZKOPÍROVÁNÍ ODKAZU, KDYŽ SCHÁNKA NENÍ.
 *
 * `navigator.clipboard` existuje jen v ZABEZPEČENÉM KONTEXTU. Na `http://`
 * (vnitřní síť, testovací adresa, obchod za proxy bez TLS) i ve starších
 * prohlížečích chybí úplně — a i tam, kde je, může uživatel oprávnění
 * odmítnout a volání skončí výjimkou.
 *
 * Proto tři stupně:
 *   1. `navigator.clipboard.writeText`,
 *   2. skryté `<textarea>` + `document.execCommand('copy')` — zastaralé,
 *      ale funguje i bez TLS a v prohlížečích, které moderní API nemají,
 *   3. když ani to ne, vrátí `false` a stránka adresu ukáže k ručnímu
 *      zkopírování. Tlačítko, které mlčky nic neudělá, je horší než tohle.
 */
async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (window.isSecureContext && navigator.clipboard !== undefined) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Odmítnuté oprávnění nebo zakázaná schránka — zkusí se náhradní cesta.
  }
  return legacyCopy(text);
}

function legacyCopy(text: string): boolean {
  if (typeof document === 'undefined') return false;

  const field = document.createElement('textarea');
  field.value = text;
  field.setAttribute('readonly', '');
  // Mimo obraz, ale ne `display:none` — skrytý prvek nejde označit.
  // `position: fixed` navíc drží stránku na místě (jinak by odskočila).
  field.style.position = 'fixed';
  field.style.top = '0';
  field.style.left = '-9999px';
  document.body.append(field);

  // Zákazníkův vlastní výběr textu se musí vrátit tam, kde byl.
  const selection = document.getSelection();
  const previous =
    selection !== null && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;

  let copied = false;
  try {
    field.select();
    // iOS ignoruje `select()` na `readonly` prvku; rozsah se musí nastavit.
    field.setSelectionRange(0, text.length);
    copied = document.execCommand('copy');
  } catch {
    copied = false;
  } finally {
    field.remove();
    if (selection !== null && previous !== null) {
      selection.removeAllRanges();
      selection.addRange(previous);
    }
  }

  return copied;
}
